import { Patterns } from '../../shared/validation/patterns';
import { ValidationUtils } from '../../shared/validation/validation-runner';
import { IValidator, Validator } from '../../shared/validation/validators';
import { FieldState } from '../../shared/models/field-state';
import { UrlConfigContainer } from '../../core/url-config';
import { UrlConfigService } from '@hrra/core';
import {
  UploadOutput,
  UploadInput,
  UploaderOptions,
} from '@angular-ex/uploader';
import { EducationLevelEnum } from '../shared/models/education-level-enum';
import { Utils } from '../../shared/utils';
import { CommonLookupItem } from '../../shared/models/common-lookup-item.model';
import { JobseekerEducationModel } from '../shared/models/jobseeker-education-model';
import { LookupService } from '../../shared/services/lookup.service';
import { JobseekerService } from '../shared/services/jobseeker.service';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { EducationalInstitutionModel } from '../../shared/models/educational-institution-model';
import { EducationalProgramModel } from '../../shared/models/educational-program-model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hr-education-editor',
  templateUrl: './education-editor.component.html',
  styleUrls: ['./education-editor.component.less'],
  standalone: false,
})
export class EducationEditorComponent implements OnInit, OnChanges {
  @Output()
  public onCancelEdit: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  public onUpdate: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input('education')
  public educationInput: JobseekerEducationModel;

  education: JobseekerEducationModel;

  public monthList: CommonLookupItem<number, string>[];
  public educationLevelList: CommonLookupItem<number, string>[];
  public educationLabelList: { [index: number]: string };
  public educationLevelEnum: EducationLevelEnum;
  public EducationLevelEnum: any = EducationLevelEnum;

  public allEducationalInstitutions: EducationalInstitutionModel[];
  public allEducationalPrograms: EducationalProgramModel[];
  public educationalInstitutions: EducationalInstitutionModel[];
  public educationalPrograms: EducationalProgramModel[];

  public selectedFileName: string;

  uploadOptions: UploaderOptions;
  uploadInput: EventEmitter<UploadInput>;

  private fieldStates: {
    dateFromMonth: FieldState;
    dateFromYear: FieldState;
    dateToMonth: FieldState;
    dateToYear: FieldState;
    institute: FieldState;
    program: FieldState;
  };

  public get field() {
    return this.fieldStates;
  }

  constructor(
    private jobseekerService: JobseekerService,
    private lookupService: LookupService,
    private translationService: TranslateService,
    private urlService: UrlConfigService<UrlConfigContainer>
  ) {
    this.educationalPrograms = [];
    this.uploadOptions = { concurrency: 1, maxUploads: 1 };
    this.uploadInput = new EventEmitter<UploadInput>();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['educationInput'] &&
      changes['educationInput'].currentValue != null
    ) {
      this.education = Utils.cloneObject(
        this.educationInput,
        JobseekerEducationModel
      );
      this.initializeFieldsState();
    }
  }

  public onCancelEducationClick() {
    this.onCancelEdit.emit(true);
  }

  onUploadOutput(output: UploadOutput): void {
    if (output.type === 'allAddedToQueue') {
      const event: UploadInput = {
        type: 'uploadAll',
        url: this.urlService.urlConfig.api.data.file.upload,
        method: 'POST',
      };
      this.uploadInput.emit(event);
    } else if (output.type === 'done' && output.file.responseStatus == 200) {
      this.selectedFileName = output.file.response.fileName;
    }
  }

  public onSaveEducationClick() {
    let education = this.education;
    var validators = this.getValidators();
    this.invokeValidation(validators, () => {
      this.jobseekerService
        .saveJobseekerEducation(education, this.selectedFileName)
        .subscribe(() => {
          this.onUpdate.emit(true);
        });
    });
  }

  invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  onLevelChange(event: any) {
    this.education.educationalInstitution = null;
    this.education.educationalProgram = null;
  }

  searchInstitute(event) {
    let query = event.query;
    this.educationalInstitutions = (
      !query
        ? this.allEducationalInstitutions
        : this.allEducationalInstitutions.filter(
            (e) => e.name.includes(query) || e.nameLatin.includes(query)
          )
    ).map((x) => Object.assign({}, x));
  }

  onInstituteSelect() {
    this.education.educationalProgram = null;
    this.allEducationalPrograms =
      this.education.educationalInstitution.programs.filter(
        (p) =>
          p.educationalLevel.educationLevelId ==
          this.education.educationLevel.educationLevelId
      );
  }

  searchProgram(event) {
    if (!this.allEducationalPrograms) return [];
    let query = event.query;
    this.educationalPrograms = (
      !query
        ? this.allEducationalPrograms
        : this.allEducationalPrograms.filter(
            (p) => p.name.includes(query) || p.nameLatin.includes(query)
          )
    ).map((x) => Object.assign({}, x));
  }

  private getFieldStatesObject(): { [index: string]: FieldState } {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }

    return result;
  }

  private getValidators() {
    let education = this.education;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );
    let dateMessage = this.translationService.instant(
      'message.StartDateMustNotComeAfterEndDate'
    );
    let incorrectFormat = this.translationService.instant(
      'message.IncorrectFormat'
    );

    let field = this.field;

    let pattern = Patterns.year;
    let regexp = new RegExp(pattern);

    let dateFromMonth = education.dateFromMonth;
    let dateToMonth = education.dateToMonth;
    var validators = [
      Validator.forRequired(
        field.dateFromYear,
        () => education.dateFromYear,
        emptyMessage
      ),
      Validator.forRequired(
        field.dateToYear,
        () => education.dateToYear,
        emptyMessage
      ),
      Validator.forPattern(
        field.dateFromYear,
        () => education.dateFromYear,
        Patterns.year,
        incorrectFormat
      ),
      Validator.forPattern(
        field.dateToYear,
        () => education.dateToYear,
        Patterns.year,
        incorrectFormat
      ),
      Validator.forPredicate(
        field.dateToMonth,
        () =>
          dateFromMonth == null ||
          dateToMonth == null ||
          education.dateFromYear == null ||
          education.dateToYear == null ||
          education.dateFromYear != education.dateToYear ||
          +dateToMonth >= +dateFromMonth,
        dateMessage
      ),
    ];

    if (regexp.test(education.dateFromYear.toString())) {
      validators.push(
        ...[
          Validator.forPredicate(
            field.dateToYear,
            () => {
              var t = +education.dateToYear >= +education.dateFromYear;
              return t;
            },
            dateMessage
          ),
        ]
      );
    }

    if (
      education.educationLevel.educationLevelId ==
        EducationLevelEnum.Bachelor ||
      education.educationLevel.educationLevelId == EducationLevelEnum.Master ||
      education.educationLevel.educationLevelId == EducationLevelEnum.Doctorate
    ) {
      validators.push(
        Validator.forRequired(
          field.institute,
          () => education.educationalInstitution,
          emptyMessage
        ),
        Validator.forPattern(
          field.institute,
          () => education.educationalInstitution,
          Patterns.textField,
          incorrectFormat
        ),
        Validator.forRequired(
          field.program,
          () => education.educationalProgram,
          emptyMessage
        ),
        Validator.forPattern(
          field.program,
          () => education.educationalProgram,
          Patterns.textField,
          incorrectFormat
        )
      );
    }

    return validators;
  }

  public onDeleteEducationAttachmentClick() {
    this.education.attachment = null;
  }

  private initializeFieldsState() {
    this.fieldStates = {
      dateFromMonth: new FieldState(() => this.education.dateFromMonth),
      dateFromYear: new FieldState(() => this.education.dateFromYear),
      dateToMonth: new FieldState(() => this.education.dateToMonth),
      dateToYear: new FieldState(() => this.education.dateToYear),
      institute: new FieldState(() => this.education.educationalInstitution),
      program: new FieldState(() => this.education.educationalProgram),
    };
  }

  ngOnInit() {
    let lookupData = this.lookupService.getCachedLookupData();
    this.monthList = lookupData.monthListFull;
    this.educationLevelList = lookupData.educationLevelList;
    this.allEducationalInstitutions = lookupData.educationalInstitutionsList;
    this.educationLabelList = {};
    this.educationLabelList[EducationLevelEnum.IncompleteHighSchool] =
      'SchoolName';
    this.educationLabelList[EducationLevelEnum.HighSchool] = 'SchoolName';
    this.educationLabelList[EducationLevelEnum.Bachelor] = 'UniversityName';
    this.educationLabelList[EducationLevelEnum.Master] = 'UniversityName';
    this.educationLabelList[EducationLevelEnum.Doctorate] = 'UniversityName';
    this.educationLabelList[EducationLevelEnum.College] = 'CollegeName';
    this.educationLabelList[EducationLevelEnum.Other] = 'InstitutionName';

    this.initEducationalFields();
  }

  private initEducationalFields() {
    if (
      this.education.educationalInstitution &&
      this.education.educationalInstitution.educationalInstitutionId
    ) {
      this.education.educationalInstitution =
        this.allEducationalInstitutions.filter(
          (i) =>
            i.educationalInstitutionId ==
            this.education.educationalInstitution.educationalInstitutionId
        )[0];
      this.allEducationalPrograms =
        this.education.educationalInstitution.programs;
      if (
        this.education.educationalProgram &&
        this.education.educationalProgram.educationalProgramId
      )
        this.education.educationalProgram = this.allEducationalPrograms.filter(
          (i) =>
            i.educationalProgramId ==
            this.education.educationalProgram.educationalProgramId
        )[0];
    } else if (this.education.institution) {
      this.education.educationalInstitution = new EducationalInstitutionModel();
      this.education.educationalInstitution.name = this.education.institution;
    }

    if (this.education.educationalProgramName) {
      this.education.educationalProgram = new EducationalProgramModel();
      this.education.educationalProgram.name =
        this.education.educationalProgramName;
    }
  }
}
